import filter from 'lodash/filter';
import moment from 'moment';
import { Container } from 'unstated';

import ToastsContainer from '~/containers/Toasts';
import {
  countResponses,
  createForm,
  deleteForm,
  deleteResponse,
  findForm,
  getForms,
  searchResponses,
  updateFormFields,
} from '~/lib/client/';
import resolve from '~/lib/forms/resolve';
import history from '~/lib/history';

export default class FormsContainer extends Container {
  state = {
    forms: [],
    loading: false,
    form: null,
    total: null,
    searchQuery: '',
    responses: [],
  };

  constructor() {
    super();
    this.routeKey = null;
    this.fetchForms();
    this.subscribeToHistory();
  }

  subscribeToHistory = () => {
    this.listener = history.listen((location) => {
      this.route(location);
    });
  };

  routes = (location) => [
    {
      path: '/forms',
      action: () => this.setForm(null),
    },
    {
      path: '/forms/:formId',
      action: (params) => this.setFormForLocation(params, location),
    },
    {
      path: '/forms/:formId/:tab',
      action: (params) => this.setFormForLocation(params, location),
    },
  ];

  route = (location) => {
    const handledRoute = resolve(this.routes(location), location);
    if (handledRoute) {
      const { route, params } = handledRoute;
      if (this.routeKey === location.key) return;
      route.action && route.action(params);
      this.routeKey = location.key;
    }
  };

  formForId = (id) => this.state.forms.find((form) => form.id === id);

  setFormForLocation = (params) => {
    const { formId } = params;
    const form = this.formForId(formId);
    this.setState({ form });
  };

  fetchForms() {
    if (this.state.forms.length == 0) {
      this.setState({ loading: true });
    }

    getForms().then((forms) => {
      this.setState({ forms, loading: false }, () => {
        this.route(history.location || window.location.pathname);
      });
    });
  }

  setForms(forms) {
    this.setState({ forms });
  }

  refreshForm = () => {
    const { form } = this.state;
    findForm(form.id).then(this.setForm);
  };

  saveForm = () => {
    const { form, forms } = this.state;

    updateFormFields(form.id, form)
      .then((updatedForm) => {
        updatedForm.isDirty = false;

        this.setState({
          form: updatedForm,
          forms: [...filter(forms, (f) => f.id !== form.id), updatedForm],
        });

        ToastsContainer.addSuccess('Form saved');
      })
      .catch(() => {
        ToastsContainer.addError('There was a problem saving this form');
      });
  };

  setForm = (form) => {
    this.setState({ form });
    if (!form) this.fetchForms();
  };

  updateForm = (form, isDirty = false) => {
    const { forms } = this.state.forms;
    if (isDirty === true) {
      form.isDirty = true;
    }
    this.setState({
      form,
      forms: [...filter(forms, (f) => f.id !== form.id), form],
    });
  };

  createForm = (attrs) => {
    this.setState({ loading: true });

    createForm(attrs).then((form) => {
      this.setState(
        {
          loading: false,
          forms: [...this.state.forms, form],
        },
        () => history.push(`/forms/${form.id}`),
      );
    });
  };

  deleteForm = (formID) => {
    deleteForm(formID).then(() => {
      const { forms } = this.state;

      ToastsContainer.addSuccess('Form deleted');

      this.setState({
        forms: [...filter(forms, (form) => form.id !== formID)],
      });
    });
  };

  setResponses = (responses) => {
    this.setState({ responses });
  };

  deleteResponse = (id) => {
    const { responses } = this.state;

    return deleteResponse(id).then(() => {
      return this.setState(
        {
          responses: filter(responses, (response) => response.id !== id),
        },
        this.fetchTotal,
      );
    });
  };

  recentResponses = () =>
    filter(this.state.responses, (response) => {
      return moment(response.submitted_at) > moment().subtract(7, 'days');
    });

  loadMoreRows = ({ startIndex, stopIndex }) => {
    const { total, form, searchQuery } = this.state;

    if (!total) {
      this.delayFetchTotal();
    }

    return searchResponses({
      start_index: startIndex,
      stop_index: stopIndex,
      form_id: form.id,
      query: searchQuery,
    }).then((responses) => {
      this.setResponses(responses);
      return responses;
    });
  };

  fetchTotal = () => {
    const { form, searchQuery } = this.state;

    countResponses({
      form_id: form.id,
      query: searchQuery,
    }).then(this.setTotal);
  };

  delayFetchTotal = (delay = 100) => {
    setTimeout(this.fetchTotal, delay);
  };

  getHash = (columns) => {
    const { form, searchQuery, total } = this.state;
    return JSON.stringify([form.id, columns, searchQuery, total]);
  };

  setTotal = ({ total }) => {
    this.setState({ total });
  };

  setQuery = (searchQuery) => {
    this.setState({
      searchQuery,
      total: null,
    });
  };

  onShowResponse = (response) => {
    this.props.onShowResponse(response);
  };
}
